import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { useFirebase } from "./contexts/FirebaseContext";
import TopMenu from "./components/TopMenu";
import HomeScreen from "./components/HomeScreen";
import DisplayNameSetup from "./components/DisplayNameSetup";
import AdminPage from "./components/AdminPage";
import { GAMES } from "./constants/games";
import useAnalytics from "./hooks/useAnalytics";
import { getDemoGameId } from "./utils/demoMode";
import {
  shouldSkipHowToPrompt,
  markHowToPromptSeen,
  clearHowToPromptSeen,
} from "./utils/howToPrompt";

// Audio file should be placed in client/public/arcade-music.mp3
const MUSIC_URL = process.env.PUBLIC_URL + "/arcade-music.mp3";

const isAdminPath = () => {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/admin";
};

function App() {
  const demoGameId = getDemoGameId();
  const [currentGame, setCurrentGame] = useState(demoGameId);
  const { points, addPoints, loading, recordGameEvent } = useFirebase();
  const [errorMessage, setErrorMessage] = useState(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [showMusicBanner, setShowMusicBanner] = useState(false);
  const [knowHowPrompt, setKnowHowPrompt] = useState(null);
  const [dontShowHowToAgain, setDontShowHowToAgain] = useState(false);
  const [howToRequest, setHowToRequest] = useState(null);
  const [autoStartGame, setAutoStartGame] = useState(false);
  const audioRef = useRef(null);
  const dontShowHowToAgainRef = useRef(false);
  const showAdmin = isAdminPath();

  const setDontShowPreference = (checked) => {
    dontShowHowToAgainRef.current = checked;
    setDontShowHowToAgain(checked);
    if (checked) {
      markHowToPromptSeen();
    } else {
      clearHowToPromptSeen();
    }
  };

  useEffect(() => {
    if (showAdmin) return undefined;

    audioRef.current = new Audio(MUSIC_URL);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.5;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [showAdmin]);

  const toggleMusic = () => {
    if (audioRef.current) {
      if (musicPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current
          .play()
          .catch((err) => console.log("Audio play failed:", err));
        setShowMusicBanner(true);
        setTimeout(() => setShowMusicBanner(false), 4000);
      }
      setMusicPlaying(!musicPlaying);
    }
  };

  const startGame = (gameId) => {
    const gameInfo = GAMES[gameId];
    if (!gameInfo) return false;
    const inDemo = Boolean(getDemoGameId());

    if (!inDemo && gameInfo.price > 0 && points < gameInfo.price) {
      setKnowHowPrompt(null);
      dontShowHowToAgainRef.current = false;
      setDontShowHowToAgain(false);
      setErrorMessage(true);
      return false;
    }

    if (!inDemo && gameInfo.price > 0) {
      addPoints(-gameInfo.price);
    }

    setKnowHowPrompt(null);
    dontShowHowToAgainRef.current = false;
    setDontShowHowToAgain(false);
    setHowToRequest(null);
    setAutoStartGame(true);
    setCurrentGame(gameId);
    recordGameEvent("game_played", { gameId });
    return true;
  };

  const openHowTo = (gameId) => {
    setKnowHowPrompt(null);
    dontShowHowToAgainRef.current = false;
    setDontShowHowToAgain(false);
    setAutoStartGame(false);
    setCurrentGame(null);
    setHowToRequest({ gameId, nonce: Date.now() });
  };

  const handleSelectGame = (gameId, options = {}) => {
    const gameInfo = GAMES[gameId];
    if (!gameInfo) return;

    const inDemo = Boolean(getDemoGameId());
    const skipPrompt =
      inDemo ||
      options.skipHowToPrompt ||
      shouldSkipHowToPrompt(gameId);

    if (skipPrompt) {
      startGame(gameId);
      return;
    }

    // Ask first — stars are only spent when they confirm play.
    dontShowHowToAgainRef.current = false;
    setDontShowHowToAgain(false);
    setKnowHowPrompt(gameId);
  };

  const handleKnowHowYes = () => {
    if (!knowHowPrompt) return;
    if (dontShowHowToAgainRef.current) {
      markHowToPromptSeen();
    }
    startGame(knowHowPrompt);
  };

  const handleKnowHowShowMe = () => {
    if (!knowHowPrompt) return;
    if (dontShowHowToAgainRef.current) {
      markHowToPromptSeen();
    }
    openHowTo(knowHowPrompt);
  };

  const handleBackToHome = () => {
    setAutoStartGame(false);
    setCurrentGame(null);
  };

  const handlePointsChange = (delta) => {
    addPoints(delta);
  };

  const currentGameInfo = currentGame ? GAMES[currentGame] : null;
  const GameComponent = currentGameInfo?.component;
  const promptGameInfo = knowHowPrompt ? GAMES[knowHowPrompt] : null;

  if (showAdmin) {
    if (loading) {
      return (
        <div className="app-container app-loading">
          <div className="app-loading-content">
            <div className="app-loading-spinner" />
            <div>Loading…</div>
          </div>
        </div>
      );
    }

    return (
      <div className="app-container is-home">
        <AdminPage />
      </div>
    );
  }

  // Demo / screen-recording mode should not wait on Firebase.
  if (loading && !demoGameId) {
    return (
      <div className="app-container app-loading">
        <div className="app-loading-content">
          <div className="app-loading-spinner" />
          <div>Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <AppContent
        currentGame={currentGame}
        points={points}
        onSelectGame={handleSelectGame}
        onOpenHowTo={openHowTo}
        onBack={handleBackToHome}
        onPointsChange={handlePointsChange}
        currentGameInfo={currentGameInfo}
        GameComponent={GameComponent}
        autoStartGame={autoStartGame}
        errorMessage={errorMessage}
        onDismissError={() => setErrorMessage(null)}
        musicPlaying={musicPlaying}
        onToggleMusic={toggleMusic}
        showMusicBanner={showMusicBanner}
        howToRequest={howToRequest}
        knowHowPrompt={knowHowPrompt}
        promptGameInfo={promptGameInfo}
        dontShowHowToAgain={dontShowHowToAgain}
        onDontShowHowToAgainChange={setDontShowPreference}
        onKnowHowYes={handleKnowHowYes}
        onKnowHowShowMe={handleKnowHowShowMe}
        onDismissKnowHow={() => {
          setKnowHowPrompt(null);
          dontShowHowToAgainRef.current = false;
          setDontShowHowToAgain(false);
        }}
      />
    </LanguageProvider>
  );
}

function AppContent({
  currentGame,
  points,
  onSelectGame,
  onOpenHowTo,
  onBack,
  onPointsChange,
  currentGameInfo,
  GameComponent,
  autoStartGame,
  errorMessage,
  onDismissError,
  musicPlaying,
  onToggleMusic,
  showMusicBanner,
  howToRequest,
  knowHowPrompt,
  promptGameInfo,
  dontShowHowToAgain,
  onDontShowHowToAgainChange,
  onKnowHowYes,
  onKnowHowShowMe,
  onDismissKnowHow,
}) {
  const { t, language, changeLanguage } = useLanguage();
  const { displayName, loading } = useFirebase();
  const [displayNameSetupState, setDisplayNameSetupState] = useState({
    open: false,
    initialName: "",
    allowCancel: false,
  });
  useAnalytics();

  useEffect(() => {
    if (loading) return;

    // Screen-recording / demo URLs skip the name gate.
    if (getDemoGameId()) {
      setDisplayNameSetupState((prev) =>
        prev.open ? { ...prev, open: false } : prev
      );
      return;
    }

    if (!displayName) {
      setDisplayNameSetupState({
        open: true,
        initialName: "",
        allowCancel: false,
      });
    } else {
      setDisplayNameSetupState((prev) =>
        prev.open ? { ...prev, open: false } : prev
      );
    }
  }, [displayName, loading]);

  const handleEditDisplayName = () => {
    setDisplayNameSetupState({
      open: true,
      initialName: displayName || "",
      allowCancel: true,
    });
  };

  if (displayNameSetupState.open) {
    return (
      <div className="app-container">
        <DisplayNameSetup
          initialName={displayNameSetupState.initialName}
          onComplete={() =>
            setDisplayNameSetupState((prev) => ({ ...prev, open: false }))
          }
          onCancel={
            displayNameSetupState.allowCancel
              ? () =>
                  setDisplayNameSetupState((prev) => ({
                    ...prev,
                    open: false,
                  }))
              : undefined
          }
        />
      </div>
    );
  }

  const isHome = currentGame === null;

  return (
    <div className={`app-container ${isHome ? "is-home" : "is-game"}`}>
      {!isHome && (
        <TopMenu
          points={points}
          gameName={currentGameInfo ? t(currentGameInfo.nameKey) : ""}
          onBack={onBack}
          showBackButton={true}
          displayName={displayName}
          onEditDisplayName={handleEditDisplayName}
          musicPlaying={musicPlaying}
          onToggleMusic={onToggleMusic}
          language={language}
          onChangeLanguage={changeLanguage}
          onHowToPlay={
            currentGame ? () => onOpenHowTo(currentGame) : undefined
          }
        />
      )}

      {errorMessage && (
        <div className="error-modal-overlay" onClick={onDismissError}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t("insufficientPoints")}</h3>
            <p>{t("insufficientPointsMessage")}</p>
            <button className="error-modal-button" onClick={onDismissError}>
              {t("ok")}
            </button>
          </div>
        </div>
      )}

      {knowHowPrompt && promptGameInfo && (
        <div
          className="error-modal-overlay"
          onClick={onDismissKnowHow}
          role="presentation"
        >
          <div
            className="error-modal know-how-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="know-how-title"
          >
            <h3 id="know-how-title">{t("knowHowTitle")}</h3>
            <p className="know-how-game">{t(promptGameInfo.nameKey)}</p>
            <p>{t("knowHowMessage")}</p>
            <label className="know-how-dont-show">
              <input
                type="checkbox"
                checked={dontShowHowToAgain}
                onChange={(event) =>
                  onDontShowHowToAgainChange(event.target.checked)
                }
              />
              <span>{t("knowHowDontShow")}</span>
            </label>
            <div className="know-how-actions">
              <button
                type="button"
                className="error-modal-button know-how-btn-secondary"
                onClick={onKnowHowShowMe}
              >
                {t("knowHowShowMe")}
              </button>
              <button
                type="button"
                className="error-modal-button"
                onClick={onKnowHowYes}
              >
                {t("knowHowYes")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isHome ? (
        <HomeScreen
          onSelectGame={onSelectGame}
          onOpenHowTo={onOpenHowTo}
          howToRequest={howToRequest}
          points={points}
          displayName={displayName}
          onEditDisplayName={handleEditDisplayName}
          language={language}
          onChangeLanguage={changeLanguage}
          musicPlaying={musicPlaying}
          onToggleMusic={onToggleMusic}
        />
      ) : (
        GameComponent && (
          <GameComponent
            onPointsChange={onPointsChange}
            autoStart={autoStartGame}
          />
        )
      )}

      {showMusicBanner && (
        <div className="music-banner">
          <span className="music-banner-icon">♪</span>
          <span className="music-banner-text">
            <strong>You</strong> — composed by James Noronha
          </span>
        </div>
      )}
    </div>
  );
}

export default App;
